import * as React from 'react'
import { DayPicker } from 'react-day-picker'

import { cn } from '@/lib/utils.ts'
import { buttonVariants } from '@/app/common/components/button/buttonVariants'
import { LeftChevronIcon, RightChevronIcon } from '@/common/icons/arrows.tsx'

import {
  Select as HeadlessSelect,
  SelectProps as HeadlessSelectProps,
} from '@headlessui/react'
import { cva, type VariantProps } from 'class-variance-authority'

export type CalendarProps = React.ComponentProps<typeof DayPicker>

type SelectProps = Omit<HeadlessSelectProps, 'size'>

const selectVariants = cva(
  'border border-gray-200 text-gray-800 rounded-md w-full',
  {
    variants: {
      size: {
        small: 'h-8 text-sm py-0 px-2',
        large: 'h-12 text-lg px-4',
      },
    },
    defaultVariants: {
      size: 'small',
    },
  }
)

interface CustomSelectProps
  extends SelectProps,
    VariantProps<typeof selectVariants> {}

function Select({ size, className, ...props }: CustomSelectProps) {
  return (
    <HeadlessSelect
      className={selectVariants({ size, className })}
      {...props}
    />
  )
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      captionLayout="dropdown"
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex  pt-1 relative text-center  w-full',
        caption_label:
          'text-sm font-medium text-gray-800 w-full  flex-shrink-0 flex-grow',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'inline-flex items-center justify-center h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        nav_button_previous: 'absolute left-1',
        caption_dropdowns: 'w-full space-y-2',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex',
        head_cell:
          'text-slate-500 rounded-md w-9 font-normal text-[0.8rem] dark:text-slate-400',
        row: 'flex w-full mt-2',
        cell: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-slate-100/50 [&:has([aria-selected])]:bg-slate-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 dark:[&:has([aria-selected].day-outside)]:bg-slate-800/50 dark:[&:has([aria-selected])]:bg-slate-800',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'inline-flex items-center justify-center shadow-none h-9 w-9 p-0 font-normal aria-selected:opacity-100 '
        ),
        day_range_end: 'day-range-end',
        day_selected:
          'bg-primary text-slate-50 hover:bg-primary hover:text-slate-50 focus:bg-primary focus:text-slate-50 dark:bg-slate-50 dark:text-slate-900 dark:hover:bg-slate-50 dark:hover:text-slate-900 dark:focus:bg-slate-50 dark:focus:text-slate-900',
        day_today:
          'bg-primary-50 text-primary-600 dark:bg-slate-800 dark:text-slate-50',
        day_outside:
          'day-outside text-slate-500 opacity-50 aria-selected:bg-slate-100/50 aria-selected:text-slate-500 aria-selected:opacity-30 dark:text-slate-400 dark:aria-selected:bg-slate-800/50 dark:aria-selected:text-slate-400',
        day_disabled: 'text-slate-500 opacity-50 dark:text-slate-400',
        day_range_middle:
          'aria-selected:bg-slate-100 aria-selected:text-slate-900 dark:aria-selected:bg-slate-800 dark:aria-selected:text-slate-50',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: () => <LeftChevronIcon className="text-lg" />,
        IconRight: () => <RightChevronIcon className="text-lg" />,
        Dropdown: (props) => {
          return <Select {...props} value={props.value} />
        },
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
